import React from 'react';
import { twMerge } from 'tailwind-merge';
import { cn } from '#/utils/classnames';

export type MultiSelectOption = {
  id: string;
  label: string;
};

export type MultiSelectProps = {
  label?: string;
  options: MultiSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  allowOther?: boolean;
  otherPlaceholder?: string;
  className?: string;
  error?: string;
  disabled?: boolean;
};

function MultiSelect(
  {
    label,
    options,
    value = [],
    onChange,
    allowOther = false,
    otherPlaceholder = 'Other (please specify)',
    className,
    error,
    disabled = false,
  }: MultiSelectProps,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const [otherValue, setOtherValue] = React.useState('');
  const [isOtherChecked, setIsOtherChecked] = React.useState(false);
  const id = React.useId();

  const handleOptionChange = (optionId: string, checked: boolean) => {
    if (checked) {
      onChange([...value, optionId]);
    } else {
      onChange(value.filter((id) => id !== optionId));
    }
  };

  const handleOtherChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setOtherValue(newValue);

    // Remove any existing "other:" values
    const filteredValue = value.filter((v) => !v.startsWith('other:'));

    if (newValue) {
      onChange([...filteredValue, `other:${newValue}`]);
    } else {
      onChange(filteredValue);
    }
  };

  return (
    <div
      ref={ref}
      className={twMerge(
        'flex flex-col gap-2',
        disabled && 'opacity-50 pointer-events-none',
        className
      )}
    >
      <fieldset>
        {label && (
          <legend className='font-medium text-base text-gray-700 dark:text-gray-300 mb-2'>
            {label}
          </legend>
        )}

        <div className='flex flex-col gap-1'>
          {options.map((option) => (
            <label
              key={option.id}
              className={cn(
                'flex items-center w-full gap-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 p-2 rounded-lg transition-colors',
                {
                  'cursor-not-allowed': disabled,
                }
              )}
            >
              <input
                type='checkbox'
                checked={value.includes(option.id)}
                onChange={(e) => handleOptionChange(option.id, e.target.checked)}
                className='d-checkbox d-checkbox-primary'
                disabled={disabled}
                id={`${id}-${option.id}`}
              />
              <span className='text-sm'>{option.label}</span>
            </label>
          ))}
          {allowOther && (
            <div className='flex items-center gap-2 p-2'>
              <input
                type='checkbox'
                checked={isOtherChecked}
                onChange={(e) => {
                  if (!e.target.checked) {
                    setOtherValue('');
                    onChange(value.filter((v) => !v.startsWith('other:')));
                    setIsOtherChecked(false);
                  } else {
                    setIsOtherChecked(true);
                  }
                }}
                className='d-checkbox d-checkbox-primary'
                id={`${id}-other`}
              />
              <input
                type='text'
                value={otherValue}
                onChange={handleOtherChange}
                placeholder={otherPlaceholder}
                className={cn('d-input d-input-primary d-input-bordered w-full', {
                  'd-input-error': error,
                })}
                disabled={!isOtherChecked}
                id={`${id}-other-text`}
                aria-label={otherPlaceholder}
              />
            </div>
          )}
        </div>

        {error && (
          <p className='text-sm text-error mt-1' role='alert'>
            {error}
          </p>
        )}
      </fieldset>
    </div>
  );
}

MultiSelect.displayName = 'MultiSelect';

export default React.forwardRef<HTMLDivElement, MultiSelectProps>(MultiSelect);
